import React, { useState, useEffect, useRef } from 'react';
import Navigation from '../components/Navigation';
import AIPrompt from '../components/AIPrompt';
import { supabase } from '../lib/supabase';
import { generateTaskDescription, OPENAI_MODELS, ANTHROPIC_MODELS, GEMINI_MODELS } from '../lib/openai';
import { toast } from '../lib/toast';
import { Wand2, Loader2, ChevronDown, ChevronUp, Star, StarOff, Copy, Download, Eye, CreditCard as Edit, Trash2, Clock, Import as SortAsc, Dessert as SortDesc, Image, X, RefreshCw, Bot, Sparkles } from 'lucide-react';
import { researchCategories } from '../lib/promptCategories';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

interface ResearchPrompt {
  id: string;
  prompt: string;
  result: string | null;
  created_at: string;
  project_id: string | null;
  llm_provider?: string;
  llm_model?: string;
}

interface CustomPrompt {
  id: string;
  title: string;
  content: string;
  category_id: string;
  created_at: string;
}

interface Agent {
  id: string;
  name: string;
  description?: string;
}

function Research({ navigate }: { navigate: (to: string) => void }) {
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>('task-management');
  const [researchHistory, setResearchHistory] = useState<ResearchPrompt[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 10;
  const [customPrompts, setCustomPrompts] = useState<CustomPrompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<string>('');
  const [showHistory, setShowHistory] = useState(true);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [viewingPrompt, setViewingPrompt] = useState<ResearchPrompt | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<ResearchPrompt | null>(null);
  const [generationType, setGenerationType] = useState<'text' | 'image'>('text');
  const [selectedModel, setSelectedModel] = useState('gpt-3.5-turbo');
  const [outputStyle, setOutputStyle] = useState<'brief' | 'detailed' | 'bullet'>('detailed');
  const [llmProvider, setLlmProvider] = useState('openai');
  const [showAgentSuggestions, setShowAgentSuggestions] = useState(false);
  const [showPromptSuggestions, setShowPromptSuggestions] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
  const [filteredPrompts, setFilteredPrompts] = useState<string[]>([]);
  const [agentQuery, setAgentQuery] = useState('');
  const [promptQuery, setPromptQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchResearchHistory(0, true);
    fetchCustomPrompts();
    fetchAgents();
    loadFavorites();
  }, []);

  const fetchAgents = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_agents')
        .select('id, name, goals')
        .order('name');

      if (error) throw error;
      
      const formattedAgents = data?.map(agent => ({
        id: agent.id,
        name: agent.name,
        description: agent.goals
      })) || [];
      
      setAgents(formattedAgents);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchResearchHistory = async (pageNum: number = 0, reset: boolean = false) => {
    try {
      if (pageNum === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const from = pageNum * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      
      const { data, error, count } = await supabase
        .from('research_prompts')
        .select('*')
        .order('created_at', { ascending: false })
        .range(from, to)
        .limit(PAGE_SIZE);

      if (error) throw error;
      
      if (reset) {
        setResearchHistory(data || []);
      } else {
        setResearchHistory(prev => [...prev, ...(data || [])]);
      }
      
      setHasMore(data?.length === PAGE_SIZE);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching research history:', error);
      toast.error('Failed to load research history');
    } finally {
      if (pageNum === 0) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const loadMoreHistory = () => {
    if (!loadingMore && hasMore) {
      fetchResearchHistory(page + 1);
    }
  };

  const fetchCustomPrompts = async () => {
    try {
      const { data, error } = await supabase
        .from('custom_prompts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCustomPrompts(data || []);
    } catch (error) {
      console.error('Error fetching custom prompts:', error);
      toast.error('Failed to load custom prompts');
    }
  };

  const loadFavorites = () => {
    const savedFavorites = localStorage.getItem('researchFavorites');
    if (savedFavorites) {
      setFavorites(new Set(JSON.parse(savedFavorites)));
    }
  };

  const saveFavorites = (newFavorites: Set<string>) => {
    localStorage.setItem('researchFavorites', JSON.stringify([...newFavorites]));
    setFavorites(newFavorites);
  };

  const toggleFavorite = (id: string) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(id)) {
      newFavorites.delete(id);
    } else {
      newFavorites.add(id);
    }
    saveFavorites(newFavorites);
  };

  const handlePromptSelect = (promptText: string) => {
    setSelectedPrompt(promptText);
    setPrompt(promptText);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setPrompt(value);
    
    // Store cursor position
    setCursorPosition(e.target.selectionStart || 0);
    
    // Get the text before the cursor
    const textBeforeCursor = value.substring(0, e.target.selectionStart || 0);
    
    // Check for @ trigger
    const atMatch = textBeforeCursor.match(/@([^\s]*)$/);
    if (atMatch) {
      const query = atMatch[1].toLowerCase();
      setAgentQuery(query);
      const filtered = agents.filter(agent => 
        agent.name.toLowerCase().includes(query)
      );
      setFilteredAgents(filtered);
      setShowAgentSuggestions(true);
      setShowPromptSuggestions(false);
    } else {
      setShowAgentSuggestions(false);
    }
    
    // Check for / trigger
    const slashMatch = textBeforeCursor.match(/\/([^\s]*)$/);
    if (slashMatch) {
      const query = slashMatch[1].toLowerCase();
      setPromptQuery(query);
      
      // Combine all prompts from categories and filter
      const allPrompts = researchCategories.flatMap(category => category.prompts);
      const customPromptTexts = customPrompts.map(p => p.content);
      const combinedPrompts = [...allPrompts, ...customPromptTexts];
      
      const filtered = combinedPrompts.filter(promptText => 
        promptText.toLowerCase().includes(query)
      );
      
      setFilteredPrompts(filtered);
      setShowPromptSuggestions(true);
      setShowAgentSuggestions(false);
    } else {
      setShowPromptSuggestions(false);
    }
  };

  const selectAgent = (agent: Agent) => {
    // Replace the @query with the selected agent
    const textBeforeCursor = prompt.substring(0, cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const textAfterCursor = prompt.substring(cursorPosition);
    
    const newPrompt = textBeforeCursor.substring(0, atIndex) + 
      `@${agent.name} ` + 
      textAfterCursor;
    
    setPrompt(newPrompt);
    setShowAgentSuggestions(false);
    
    // Focus the textarea and set cursor position after the inserted agent name
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newPosition = atIndex + agent.name.length + 2; // +2 for @ and space
        textareaRef.current.setSelectionRange(newPosition, newPosition);
        setCursorPosition(newPosition);
      }
    }, 0);
  };

  const selectPrompt = (promptText: string) => {
    // Replace the /query with the selected prompt
    const textBeforeCursor = prompt.substring(0, cursorPosition);
    const slashIndex = textBeforeCursor.lastIndexOf('/');
    const textAfterCursor = prompt.substring(cursorPosition);
    
    const newPrompt = textBeforeCursor.substring(0, slashIndex) + promptText + textAfterCursor;
    
    setPrompt(newPrompt);
    setShowPromptSuggestions(false);
    
    // Focus the textarea and set cursor position after the inserted prompt
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newPosition = slashIndex + promptText.length;
        textareaRef.current.setSelectionRange(newPosition, newPosition);
        setCursorPosition(newPosition);
      }
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) {
      toast.error('Please enter a research prompt');
      return;
    }

    setLoading(true);
    setResult(null);
    
    try {
      if (generationType === 'image') {
        // Generate image
        const response = await fetch('https://api.openai.com/v1/images/generations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await getOpenAIKey()}`
          },
          body: JSON.stringify({
            model: "dall-e-3",
            prompt,
            n: 1,
            size: "1024x1024"
          })
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error?.message || 'Failed to generate image');
        }
        
        const data = await response.json();
        const imageUrl = data.data[0]?.url;
        
        if (!imageUrl) {
          throw new Error('No image URL returned');
        }
        
        // Save image URL as HTML in result
        const imageHtml = `<div class="flex justify-center"><img src="${imageUrl}" alt="Generated image" class="max-w-full rounded-lg shadow-lg" /></div>`;
        setResult(imageHtml);
        
        // Save to research_prompts
        await saveResearch(prompt, imageHtml, 'openai', 'dall-e-3');
        
      } else { // Text generation
        // Generate text
        const response = await generateTaskDescription(prompt, 'research', outputStyle);
        setResult(response);
        
        // Save to research_prompts
        await saveResearch(prompt, response, 'openai', selectedModel);
        
        // Refresh history
        fetchResearchHistory(0, true);
        
      }
      
    } catch (error) {
      console.error('Error generating research:', error);
      toast.error('Failed to generate research');
    } finally {
      setLoading(false);
    }
  };

  const getOpenAIKey = async (): Promise<string> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('llm_key')
        .eq('user_id', session.user.id)
        .single();

      if (error) throw error;
      if (!data?.llm_key) throw new Error('No OpenAI API key found');
      
      return data.llm_key;
    } catch (error) {
      console.error('Error getting OpenAI key:', error);
      throw error;
    }
  };

  const saveResearch = async (promptText: string, resultText: string, provider?: string, model?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('research_prompts')
        .insert([{
          prompt: promptText,
          result: resultText,
          user_id: user.id,
          llm_provider: provider || 'openai',
          llm_model: model || selectedModel
        }]);

      if (error) throw error;
    } catch (error) {
      console.error('Error saving research:', error);
      toast.error('Failed to save research');
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this research?')) return;
    
    try {
      const { error } = await supabase
        .from('research_prompts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setResearchHistory(researchHistory.filter(item => item.id !== id));
      
      // If we've deleted an item and the list is getting short, fetch more
      if (researchHistory.length <= 5 && hasMore) {
        fetchResearchHistory(0, true);
      }
      
      toast.success('Research deleted successfully');
    } catch (error) {
      console.error('Error deleting research:', error);
      toast.error('Failed to delete research');
    }
  };

  const handleDownload = async (item: ResearchPrompt) => {
    try {
      // Check if the result contains an image
      if (item.result?.includes('<img')) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(item.result || '', 'text/html');
        const img = doc.querySelector('img');
        
        if (img && img.src) {
          // Download as image
          const link = document.createElement('a');
          link.href = img.src;
          link.download = `research-${new Date().toISOString().slice(0, 10)}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }
      }

      // Create a temporary div to render the HTML content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = item.result || '';
      tempDiv.style.padding = '20px';
      tempDiv.style.maxWidth = '800px';
      tempDiv.style.margin = '0 auto';
      tempDiv.style.fontFamily = 'Arial, sans-serif';
      document.body.appendChild(tempDiv);

      try {
        // Use html2canvas to capture the rendered HTML
        const canvas = await html2canvas(tempDiv, {
          scale: 2, // Higher scale for better quality
          useCORS: true,
          logging: false,
          allowTaint: true
        });

        // Create PDF with proper dimensions
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm'
        });

        // Set up page dimensions
        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 20;
        const lineHeight = 7;
        
        // Add header with title
        pdf.setFillColor(79, 70, 229); // Indigo color
        pdf.rect(0, 0, pageWidth, 40, 'F');
        
        // Add title with white text
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(22);
        pdf.setFont('helvetica', 'bold');
        const title = 'Research Results';
        const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
        const titleX = (pageWidth - titleWidth) / 2;
        pdf.text(title, titleX, 25);
        
        // Reset text color for content
        pdf.setTextColor(0, 0, 0);
        
        // Start position after header
        let y = 50;
        
        // Add prompt section
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setFillColor(240, 247, 255); // Light blue background
        pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
        pdf.text('Prompt:', margin, y);
        y += 10;
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        const wrappedPrompt = pdf.splitTextToSize(item.prompt, pageWidth - (margin * 2));
        pdf.text(wrappedPrompt, margin, y);
        y += wrappedPrompt.length * lineHeight + 15;
        
        // Parse HTML content
        const parser = new DOMParser();
        const doc = parser.parseFromString(item.result || '', 'text/html');
        
        // Process each heading and its content
        const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
        
        if (headings.length === 0) {
          // If no headings, just add the text content
          const content = doc.body.textContent || '';
          const wrappedContent = pdf.splitTextToSize(content, pageWidth - (margin * 2));
          
          // Check if we need a new page
          if (y + wrappedContent.length * lineHeight > pdf.internal.pageSize.getHeight() - 20) {
            pdf.addPage();
            y = 20;
          }
          
          pdf.text(wrappedContent, margin, y);
        } else {
          headings.forEach((heading) => {
            // Check if we need a new page
            if (y > pdf.internal.pageSize.getHeight() - 40) {
              pdf.addPage();
              y = 20;
            }
            
            // Set heading font
            const level = parseInt(heading.tagName.substring(1));
            pdf.setFontSize(16 - (level - 1) * 2); // h1=16, h2=14, h3=12, etc.
            pdf.setFont('helvetica', 'bold');
            
            // Add background for headings
            if (level <= 3) {
              pdf.setFillColor(240, 247, 255); // Light blue background
              pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
            }
            
            pdf.text(heading.textContent || '', margin, y);
            y += 12;
            
            // Process content until next heading
            let nextElement = heading.nextElementSibling;
            while (nextElement && !['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(nextElement.tagName)) {
              // Check if we need a new page
              if (y > pdf.internal.pageSize.getHeight() - 20) {
                pdf.addPage();
                y = 20;
              }
              
              if (nextElement.tagName === 'P') {
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'normal');
                const wrappedText = pdf.splitTextToSize(nextElement.textContent || '', pageWidth - (margin * 2));
                pdf.text(wrappedText, margin, y);
                y += wrappedText.length * lineHeight;
              } else if (nextElement.tagName === 'UL' || nextElement.tagName === 'OL') {
                // Process list items
                const items = nextElement.querySelectorAll('li');
                items.forEach((item, index) => {
                  // Check if we need a new page
                  if (y > pdf.internal.pageSize.getHeight() - 20) {
                    pdf.addPage();
                    y = 20;
                  }
                  
                  pdf.setFontSize(11);
                  pdf.setFont('helvetica', 'normal');
                  
                  // Add bullet point or number
                  const prefix = nextElement.tagName === 'OL' ? `${index + 1}.` : '•';
                  pdf.text(prefix, margin, y);
                  
                  const wrappedItem = pdf.splitTextToSize(item.textContent || '', pageWidth - (margin * 2) - 10);
                  pdf.text(wrappedItem, margin + 10, y);
                  y += wrappedItem.length * lineHeight;
                });
              } else if (nextElement.tagName === 'PRE') {
                // Code blocks
                pdf.setFontSize(10);
                pdf.setFont('courier', 'normal');
                pdf.setFillColor(245, 247, 250);
                
                const codeText = nextElement.textContent || '';
                const wrappedCode = pdf.splitTextToSize(codeText, pageWidth - (margin * 2) - 10);
                
                // Background for code
                pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, wrappedCode.length * lineHeight + 10, 'F');
                
                pdf.text(wrappedCode, margin, y);
                y += wrappedCode.length * lineHeight + 5;
                
                // Reset font
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(11);
              } else if (nextElement.tagName === 'IMG') {
                // Handle images
                const img = nextElement as HTMLImageElement;
                if (img.src) {
                  try {
                    // Calculate image dimensions to fit the page width
                    const imgWidth = pageWidth - (margin * 2);
                    const imgHeight = img.height * (imgWidth / img.width);
                    
                    // Check if we need a new page
                    if (y + imgHeight > pdf.internal.pageSize.getHeight() - 20) {
                      pdf.addPage();
                      y = 20;
                    }
                    
                    // Add the image
                    pdf.addImage(img.src, 'JPEG', margin, y, imgWidth, imgHeight);
                    y += imgHeight + 10;
                  } catch (imgError) {
                    console.error('Error adding image to PDF:', imgError);
                    // Add a placeholder text instead
                    pdf.text('[Image could not be included]', margin, y);
                    y += lineHeight + 5;
                  }
                }
              }
              
              nextElement = nextElement.nextElementSibling;
              y += 3; // Add some space between elements
            }
            
            y += 8; // Add space after section
          });
        }
        
        // Add footer with date
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Generated on ${new Date().toLocaleDateString()} | Research Results`, margin, pdf.internal.pageSize.getHeight() - 10);

        pdf.save(`research-${new Date().toISOString().slice(0, 10)}.pdf`);
      } finally {
        // Clean up
        document.body.removeChild(tempDiv);
      }
    } catch (error) {
      console.error('Error downloading research:', error);
      toast.error('Failed to download research');
    }
  };

  const handleViewPrompt = (item: ResearchPrompt) => {
    setViewingPrompt(item);
  };

  const handleEditPrompt = (item: ResearchPrompt) => {
    setEditingPrompt(item);
    setPrompt(item.prompt);
    setResult(item.result);
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sortedHistory = [...researchHistory].sort((a, b) => {
    if (sortOrder === 'asc') {
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    } else {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
  });

  // Get custom prompts for the "others" category
  const customPromptsForCategory = customPrompts.filter(p => p.category_id === 'others');
  
  // Update the "others" category with custom prompts
  const categoriesWithCustomPrompts = researchCategories.map(category => {
    if (category.id === 'others') {
      return {
        ...category,
        prompts: customPromptsForCategory.map(p => p.content)
      };
    }
    return category;
  });

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Research Assistant</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Prompt Categories */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg overflow-hidden">
                <div className="p-6">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Prompt Categories</h2>
                  
                  <div className="space-y-4">
                    {categoriesWithCustomPrompts.map((category) => (
                      <div key={category.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                          className="w-full flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          <span className="font-medium text-gray-900 dark:text-white">{category.title}</span>
                          {expandedCategory === category.id ? (
                            <ChevronUp className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                        
                        {expandedCategory === category.id && (
                          <div className="p-4 space-y-3">
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{category.description}</p>
                            
                            {category.prompts.length > 0 ? (
                              <div className="space-y-2">
                                {category.prompts.map((promptText, index) => (
                                  <button
                                    key={index}
                                    onClick={() => handlePromptSelect(promptText)}
                                    className="w-full text-left p-3 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                  >
                                    {promptText}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 dark:text-gray-400 italic">No prompts available</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Research Input and Results */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg overflow-hidden mb-8">
                <div className="p-6">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Research Input</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="relative">
                      <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                        Enter your research prompt
                      </label>
                      <textarea
                        id="prompt"
                        rows={4}
                        ref={textareaRef}
                        value={prompt}
                        onChange={handleInputChange}
                        onKeyDown={(e) => {
                          // Handle escape key to close suggestions
                          if (e.key === 'Escape') {
                            setShowAgentSuggestions(false);
                            setShowPromptSuggestions(false);
                          }
                        }}
                        placeholder="Enter your research question or topic..."
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      ></textarea>
                      
                      {/* Agent Suggestions */}
                      {showAgentSuggestions && filteredAgents.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto">
                          <ul className="py-1">
                            {filteredAgents.map(agent => (
                              <li 
                                key={agent.id}
                                onClick={() => selectAgent(agent)}
                                className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                              >
                                <div className="flex items-center">
                                  <Bot className="h-4 w-4 text-indigo-500 mr-2" />
                                  <span className="font-medium">{agent.name}</span>
                                </div>
                                {agent.description && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                                    {agent.description}
                                  </p>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Prompt Suggestions */}
                      {showPromptSuggestions && filteredPrompts.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto">
                          <ul className="py-1">
                            {filteredPrompts.map((promptText, index) => (
                              <li 
                                key={index}
                                onClick={() => selectPrompt(promptText)}
                                className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                              >
                                <div className="flex items-center">
                                  <Sparkles className="h-4 w-4 text-indigo-500 mr-2" />
                                  <span className="text-sm">{promptText}</span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex space-x-4">
                        <span>Type <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">@</kbd> to use an agent</span>
                        <span>Type <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">/</kbd> to use a prompt</span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="w-full sm:w-1/4">
                        <label htmlFor="generation-type" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                          Generation Type
                        </label>
                        <div className="mt-1 flex rounded-md shadow-sm">
                          <button
                            type="button"
                            onClick={() => setGenerationType('text')}
                            className={`flex-1 py-2 px-4 text-sm font-medium rounded-l-md ${
                              generationType === 'text'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                            }`}
                            disabled={loading}
                          >
                            Text
                          </button>
                          <button
                            type="button"
                            onClick={() => setGenerationType('image')}
                            className={`flex-1 py-2 px-4 text-sm font-medium rounded-r-md ${
                              generationType === 'image'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                            }`}
                            disabled={loading}
                          >
                            <Image className="h-4 w-4 inline-block mr-1" />
                            Image
                          </button>
                        </div>
                      </div>

                      {generationType === 'text' && (
                        <>
                          <div className="w-full sm:w-1/4">
                            <label htmlFor="llm-provider" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                              LLM Provider
                            </label>
                            <select
                              id="llm-provider"
                              value={llmProvider}
                              onChange={(e) => setLlmProvider(e.target.value)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            >
                              <option value="openai">OpenAI</option>
                              <option value="anthropic">Anthropic</option>
                              <option value="gemini">Google Gemini</option>
                            </select>
                          </div>

                          <div className="w-full sm:w-1/4">
                            <label htmlFor="model-select" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                              Model
                            </label>
                            <select
                              id="model-select"
                              value={selectedModel}
                              onChange={(e) => setSelectedModel(e.target.value)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              disabled={loading}
                            >
                              <option value="auto">Auto-select best model</option>
                              {llmProvider === 'openai' && (
                                <>
                                  {Object.entries(OPENAI_MODELS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                  ))}
                                </>
                              )}
                              {llmProvider === 'anthropic' && (
                                <>
                                  {Object.entries(ANTHROPIC_MODELS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                  ))}
                                </>
                              )}
                              {llmProvider === 'gemini' && (
                                <>
                                  {Object.entries(GEMINI_MODELS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                  ))}
                                </>
                              )}
                            </select>
                          </div>

                          <div className="w-full sm:w-1/4">
                            <label htmlFor="output-style" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                              Output Style
                            </label>
                            <select
                              id="output-style"
                              value={outputStyle}
                              onChange={(e) => setOutputStyle(e.target.value as 'brief' | 'detailed' | 'bullet')}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              disabled={loading}
                            >
                              <option value="brief">Short Response (3-4 lines)</option>
                              <option value="bullet">Bullet Points</option>
                              <option value="detailed">Long Structured</option>
                            </select>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="animate-spin h-5 w-5 mr-2" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Wand2 className="h-5 w-5 mr-2" />
                            Generate {generationType === 'image' ? 'Image' : 'Research'}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {result && (
                <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg overflow-hidden mb-8">
                  <div className="p-6">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{result.includes('<img') ? 'Generated Image' : 'Research Results'}</h2>
                    <div ref={resultRef} className="prose prose-sm dark:prose-invert max-w-none overflow-x-auto research-content">
                      <div dangerouslySetInnerHTML={{ __html: result }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Research History */}
              <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg overflow-hidden mb-8">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      className="flex items-center text-lg font-medium text-gray-900 dark:text-white"
                    >
                      <h2>Research History</h2>
                      {showHistory ? (
                        <ChevronUp className="h-5 w-5 ml-2" />
                      ) : (
                        <ChevronDown className="h-5 w-5 ml-2" />
                      )}
                    </button>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                        title={sortOrder === 'desc' ? 'Oldest first' : 'Newest first'}
                      >
                        {sortOrder === 'desc' ? (
                          <SortDesc className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        ) : (
                          <SortAsc className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        )}
                      </button>
                      <button
                        onClick={() => fetchResearchHistory(0, true)}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Refresh history"
                      >
                        <RefreshCw className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      </button>
                    </div>
                  </div>
                  
                  {showHistory && (
                    <div className="space-y-4">
                      {sortedHistory.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-4">No research history yet</p>
                      ) : (
                        sortedHistory.map((item) => (
                          <div 
                            key={item.id} 
                            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h3 className="font-medium text-gray-900 dark:text-white mb-1 line-clamp-1">
                                  {item.prompt}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {new Date(item.created_at).toLocaleString()}
                                  {item.llm_model && (
                                    <span className="ml-2 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                                      {item.llm_model}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => handleViewPrompt(item)}
                                  className="p-1 text-gray-400 hover:text-indigo-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                  title="View"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleEditPrompt(item)}
                                  className="p-1 text-gray-400 hover:text-indigo-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleCopy(item.result || '')}
                                  className="p-1 text-gray-400 hover:text-indigo-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                  title="Copy to clipboard"
                                >
                                  <Copy className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDownload(item)}
                                  className="p-1 text-gray-400 hover:text-indigo-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => toggleFavorite(item.id)}
                                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                  title={favorites.has(item.id) ? 'Remove from favorites' : 'Add to favorites'}
                                >
                                  {favorites.has(item.id) ? (
                                    <Star className="h-4 w-4 text-yellow-400" />
                                  ) : (
                                    <StarOff className="h-4 w-4 text-gray-400 hover:text-yellow-400" />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  className="p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}

                      {hasMore && (
                        <div className="flex justify-center pt-4">
                          <button
                            onClick={loadMoreHistory}
                            disabled={loadingMore}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                          >
                            {loadingMore ? (
                              <>
                                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                                Loading...
                              </>
                            ) : (
                              'Load More'
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* View Prompt Modal */}
      {viewingPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Research Details</h3>
                <button
                  onClick={() => setViewingPrompt(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Prompt</h4>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{viewingPrompt.prompt}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Result</h4>
                  <div className="mt-1 prose prose-sm dark:prose-invert max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: viewingPrompt.result || '' }} />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleCopy(viewingPrompt.result || '')}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </button>
                  <button
                    onClick={() => handleDownload(viewingPrompt)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Research;